import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Share2, Camera, PenTool, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const icons = [Share2, Camera, PenTool];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section 
      id="servicos" 
      data-testid="services-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 bg-[#eaddcf]/30"
    >
      <div className="container-main">
        {/* Section header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs md:text-sm font-medium uppercase tracking-widest text-[#ff00c7] mb-4 block"
          >
            {t.services.badge}
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="services-title"
            className="font-['Syne'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#242328] leading-tight mb-6"
          >
            {t.services.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base md:text-lg text-[#242328]/70 leading-relaxed"
          >
            {t.services.description}
          </motion.p>
        </div>

        {/* Services grid - Bento style */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {t.services.items.map((service, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={service.title}
                variants={cardVariants}
                data-testid={`service-card-${index}`}
                className="service-card group bg-white/60 backdrop-blur-sm rounded-2xl p-8 flex flex-col h-full text-center md:text-left"
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-[#242328] flex items-center justify-center mb-6 group-hover:bg-[#ff00c7] transition-colors duration-300 mx-auto md:mx-0">
                  <Icon className="w-6 h-6 text-[#f8f6e2]" strokeWidth={1.5} />
                </div>

                {/* Content */}
                <h3 className="font-['Syne'] text-xl md:text-2xl font-bold text-[#242328] mb-4">
                  {service.title}
                </h3>

                <p className="text-[#242328]/70 leading-relaxed mb-6 flex-grow">
                  {service.description}
                </p>

                {/* Benefits */}
                <ul className="space-y-2 mb-6">
                  {service.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-2 text-sm text-[#242328]/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#ff00c7]" />
                      {benefit}
                    </li>
                  ))}
                </ul>

                {/* Arrow link */}
                <div className="flex items-center gap-2 text-[#242328] group-hover:text-[#ff00c7] transition-colors duration-300 mt-auto">
                  <span className="text-sm font-medium">{t.services.learnMore}</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
